// src/pages/Etv.jsx
import { useEffect, useRef } from "react";
import {
  FaChalkboardTeacher,
  FaBullhorn,
  FaImage,
  FaClock,
  FaQuran,
  FaBroadcastTower,
  FaCheckCircle,
  FaBolt,
  FaMobileAlt,
} from "react-icons/fa";

import "../styles/Pengenalan.css";
import "../styles/Etv.css";

const HERO_POSTER = "/assets/etv_poster.jpg";
const HERO_SOURCES = [
  { src: "/assets/etv_demo.webm", type: "video/webm" },
  { src: "/assets/etv_demo.mp4", type: "video/mp4" },
];

const features = [
  {
    icon: <FaChalkboardTeacher />,
    title: "Maklumat Kuliah",
    desc: "Jadual kuliah & ceramah terkini terus ke TV masjid.",
  },
  {
    icon: <FaBullhorn />,
    title: "Pengumuman Segera",
    desc: "Umumkan info penting secara langsung & real-time.",
  },
  {
    icon: <FaImage />,
    title: "Poster & Infografik",
    desc: "Paparkan poster, video, hadis & info bermanfaat setiap hari.",
  },
  {
    icon: <FaClock />,
    title: "Waktu Solat Automatik",
    desc: "Paparan waktu solat digital terus dikemas kini setiap hari.",
  },
  {
    icon: <FaQuran />,
    title: "Ayat al-Quran & Tafsir",
    desc: "Ayat pilihan bersama tafsir ringkas & terjemahan.",
  },
  {
    icon: <FaBroadcastTower />,
    title: "Live Masjidil Haram",
    desc: "Tonton siaran langsung dari Mekah & Madinah.",
  },
];

function TVDemo() {
  const vRef = useRef(null);

  useEffect(() => {
    const v = vRef.current;
    if (!v) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;

        v.querySelectorAll("source").forEach((s) => {
          if (s.dataset.src) s.src = s.dataset.src;
        });

        v.load();
        v.play().catch(() => { });
        io.disconnect();
      },
      { rootMargin: "200px" }
    );

    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <div className="etvpg-tv">
      <div className="etvpg-tv-top">
        <div className="etvpg-pill" />
        <div className="etvpg-pill" />
        <div className="etvpg-pill" />
      </div>

      <div className="etvpg-tv-body">
        <div className="etvpg-tv-title">Demo Paparan ETV</div>
        <div className="etvpg-tv-sub">
          Poster, video, waktu solat, ayat & pengumuman — semuanya dalam satu paparan.
        </div>

        <div className="etvpg-tv-screen">
          <video
            ref={vRef}
            poster={HERO_POSTER}
            muted
            loop
            playsInline
            preload="none"
            aria-label="Demo ETV"
          >
            {HERO_SOURCES.map((s) => (
              <source key={s.type} data-src={s.src} type={s.type} />
            ))}
          </video>
        </div>

        <div className="etvpg-tv-note">
          * Video demo akan auto-load bila scroll dekat bahagian ini.
        </div>
      </div>
    </div>
  );
}

export default function Etv() {
  return (
    <div className="pg-wrap">
      <div className="container pg-container">
        {/* HERO */}
        <div className="pg-hero">
          <div>
            <h1 className="pg-h1">Solusi Digital TV Masjid</h1>

            <p className="pg-lead">
              <b>Etikaf TV (ETV)</b> ialah platform TV masjid generasi baharu —
              info masjid, waktu solat, kira detik masuk waktu solat, poster dan video,
              hitung hari kebesaran Islam, ayat al-Quran, pengumuman & siaran langsung
              dari Masjidil Haram. Semuanya automatik dan boleh dikawal dari pelbagai
              peranti serta dari jauh.
            </p>



            <div className="pg-quicklinks">
              <a className="pg-chip" href="#etv-demo">Demo</a>
              <a className="pg-chip" href="#etv-features">Ciri</a>
            </div>
          </div>

          {/* RIGHT: side card */}
          <div className="pg-side-card">
            <div className="pg-side-head">
              <div className="pg-side-ic">
                <FaBolt />
              </div>
              <div>
                <div className="pg-side-title">Ringkas, kemas & automatik</div>
                <div className="pg-side-sub">Setup mudah — terus jalan</div>
              </div>
            </div>

            <div className="pg-side-body">
              <div className="pg-side-mini">
                <div className="pg-mini-row">
                  <FaCheckCircle /> Auto jadual & pengisian
                </div>
                <div className="pg-mini-row">
                  <FaMobileAlt /> Kawalan dari jauh (multi-peranti)
                </div>
                <div className="pg-mini-row">
                  <FaClock /> Waktu solat & countdown
                </div>
              </div>

              <div className="pg-note" style={{ marginTop: 12 }}>
                Sesuai untuk TV masjid, skrin foyer, dewan solat, atau ruang aktiviti.
              </div>
            </div>
          </div>
        </div>

        {/* DEMO */}
        <div className="pg-section" id="etv-demo">
          <div className="pg-section-title">
            <span className="pg-kicker">Demo</span>
            <h2 className="pg-h2">Contoh Paparan ETV</h2>
            <p className="pg-desc">
              Inilah gaya paparan yang biasanya berjalan automatik di TV masjid.
            </p>
          </div>

          <TVDemo />
        </div>

        {/* FEATURES */}
        <div className="pg-section" id="etv-features">
          <div className="pg-section-title">
            <span className="pg-kicker">Ciri Utama</span>
            <h2 className="pg-h2">Apa yang ETV boleh buat?</h2>
            <p className="pg-desc">
              Fokus kepada keperluan masjid — maklumat jelas, kemas, dan mudah dikawal.
            </p>
          </div>

          <div className="pg-grid-3">
            {features.map((f) => (
              <div className="pg-card" key={f.title}>
                <div className="pg-card-icon">{f.icon}</div>
                <div>
                  <div className="pg-card-title">{f.title}</div>
                  <div className="pg-card-text">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>




      </div>
    </div>
  );
}
